import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {SwapHoriz,} from '@mui/icons-material';
import AdminSidebar from "../../components/admin/AdminSidebar";
import DashboardHeader from "../../components/admin/DashboardHeader";
import TransactionModal from "../../components/admin/transactions/TransactionModal";
import TransactionTable from "../../components/admin/transactions/TransactionTable";
import TransactionFilters from "../../components/admin/transactions/TransactionFilters";
import TransactionTabs from "../../components/admin/transactions/TransactionTabs";
import TransactionStats from "../../components/admin/transactions/TransactionStats";
import { useAuth } from "../../contexts/AuthContext";
import { fetchTransactions, formatDateForAPI, displayReference, displayCustomer } from "../../services/transactionService";
import { fetchOrganizationUserRoles, mapUserRoleRecord } from "../../services/userRoleService";
import { fetchCompanyPos, formatTerminalOptionLabel } from "../../services/posService";
import { isOrganizationAdmin } from "../../utils/organizationRole";
import { classifyTransactionType, buildTransactionCategoryIndex } from "../../utils/transactionCategory";
import { resolveOperator } from "../../utils/operator";

const isCashierRole = (role) => {
    const r = String(role || '').toLowerCase();
    return r === 'cashier' || r === 'caissier';
};

const TransactionsPage = () => {
    const { selectedOrganization, user } = useAuth();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [timeRange, setTimeRange] = useState('today');
    const [currency, setCurrency] = useState('CDF');
    const [activeTab, setActiveTab] = useState('all');
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [transactionsData, setTransactionsData] = useState({
        all: [],
        paymentRequests: [],
        invoices: [],
        paymentLinks: [],
        withdrawals: [],
        deposits: []
    });
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        search: '',
        status: 'all',
        operation: 'all',
        service: 'all',      // srv
        caisse: 'all',       // terminal → act = caissier du terminal
        ref: 'all'           // ref
    });
    const [cashiers, setCashiers] = useState([]);
    const [terminals, setTerminals] = useState([]);
    const [nextPage, setNextPage] = useState(null);
    const [prevPage, setPrevPage] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [currencyLoading, setCurrencyLoading] = useState(false);
    const hasLoadedOnceRef = useRef(false);
    const loadTransactionsRef = useRef(null);

    const isAdmin = isOrganizationAdmin(selectedOrganization);

    const cashierNameById = useMemo(() => {
        const map = {};
        cashiers.forEach((c) => {
            if (c.id) map[c.id] = c.name;
        });
        return map;
    }, [cashiers]);

    const loadCashiers = useCallback(async () => {
        if (!selectedOrganization?.id || !isAdmin) {
            setCashiers([]);
            return;
        }
        try {
            const response = await fetchOrganizationUserRoles(selectedOrganization.id, {
                pge: 1,
                lmt: 100,
                sts: 'active',
                usr: 'all',
                rol: 'all',
            });
            const list = (response.contents || [])
                .filter((item) => item.user != null)
                .map(mapUserRoleRecord)
                .filter((m) => isCashierRole(m.role) && String(m.status || '').toLowerCase() === 'active')
                .map((m) => ({
                    id: m.id,
                    name: m.name || m.phone || m.id,
                }));
            setCashiers(list);
        } catch (error) {
            console.error('Error loading cashiers:', error);
            setCashiers([]);
        }
    }, [selectedOrganization?.id, isAdmin]);

    useEffect(() => {
        loadCashiers();
    }, [loadCashiers]);

    const loadTerminals = useCallback(async () => {
        if (!selectedOrganization?.id || !isAdmin) {
            setTerminals([]);
            return;
        }
        try {
            setTerminals(await fetchCompanyPos(selectedOrganization.id));
        } catch (error) {
            console.error('Error loading terminals:', error);
            setTerminals([]);
        }
    }, [selectedOrganization?.id, isAdmin]);

    useEffect(() => {
        loadTerminals();
    }, [loadTerminals]);

    /** Caisses proposées dans le filtre : terminal + caissier assigné. */
    const caisses = useMemo(() => terminals.map((t) => ({
        id: t.id,
        name: formatTerminalOptionLabel(t),
        cashierId: t.managerId || null,
        cashierName: t.managerId ? (cashierNameById[t.managerId] || null) : null,
    })), [terminals, cashierNameById]);

    const categoryIndex = useMemo(
        () => buildTransactionCategoryIndex({ terminalIds: terminals.map((t) => t.id) }),
        [terminals]
    );

    const caisseById = useMemo(() => {
        const map = {};
        caisses.forEach((c) => { map[c.id] = c; });
        return map;
    }, [caisses]);

    /** Un caissier peut gérer plusieurs terminaux : on garde tous les libellés. */
    const caisseNamesByCashier = useMemo(() => {
        const map = {};
        caisses.forEach((c) => {
            if (!c.cashierId) return;
            map[c.cashierId] = map[c.cashierId] ? `${map[c.cashierId]}, ${c.name}` : c.name;
        });
        return map;
    }, [caisses]);

    const loadTransactions = useCallback(async ({ silent = false } = {}) => {
        if (!selectedOrganization) {
            setLoading(false);
            setCurrencyLoading(false);
            return;
        }

        try {
            // Sur changement de devise/période/filtre, on garde l'UI et on recharge en arrière-plan
            const shouldSilent = silent || hasLoadedOnceRef.current;
            if (shouldSilent) {
                setCurrencyLoading(true);
            } else {
                setLoading(true);
            }
            const solutionId = localStorage.getItem('netikash_solution');
            
            // Calculer les dates selon la période
            const now = new Date();
            let startDate = new Date();
            
            if (timeRange === 'today') {
                startDate.setHours(0, 0, 0, 0);
            } else if (timeRange === 'week') {
                startDate.setDate(now.getDate() - 7);
            } else if (timeRange === 'month') {
                startDate.setMonth(now.getMonth() - 1);
            }

            const admin = isOrganizationAdmin(selectedOrganization);

            // Construire les paramètres de requête (comme sur mobile)
            const params = {
                own: selectedOrganization.id,  // Sur mobile: own = currentOrg.id
                sol: solutionId,
                srv: filters.service || 'all',  // Service (srv)
                // Admin : toutes les transactions de l'org, ou celles du caissier de la caisse choisie
                // (le backend trs ne filtre que par accountable). Caissier : uniquement les siennes.
                act: admin
                    ? (caisseById[filters.caisse]?.cashierId || 'all')
                    : (user?.id || 'all'),
                ref: filters.ref || 'all',      // Référence (ref)
                opr: filters.operation || 'all', // Opération
                slug: filters.search || '',      // Recherche (slug)
                // Toutes devises : la liste montre chaque transaction dans sa devise, la bascule CDF/USD ne pilote que les statistiques
                crc: 'all',
                sts: filters.status || 'all',    // Statut (sts)
                fdt: formatDateForAPI(startDate),
                tdt: formatDateForAPI(now),
                lmt: 20,                        // Limite (lmt)
                pge: currentPage                 // Page (pge)
            };

            // Les onglets filtrent côté client : l'API attend un code d'opération (PMT…), pas 'payment',
            // et renvoyait donc une liste vide dès qu'on changeait d'onglet.

            const response = await fetchTransactions(params);
            const transactions = response.contents || [];
            
            // Pagination bidirectionnelle (comme sur mobile)
            setNextPage(response.nextPage || null);
            setPrevPage(response.previousPage || null);

            // Transformer les transactions pour correspondre au format attendu
            const transformTransaction = (t) => {
                const transactionDate = t.state?.requested_at || t.createdAt || new Date();
                const dateObj = new Date(transactionDate);
                const accountableId = t.accountable || t.maker || null;
                const cashierName = accountableId
                    ? (cashierNameById[accountableId] || accountableId)
                    : '—';
                // Demande de paiement POS : service = id du terminal ; sinon caisse(s) du caissier
                const caisseName = caisseById[t.service]?.name
                    || (accountableId ? caisseNamesByCashier[accountableId] : null)
                    || null;
                
                const { type, typeLabel } = classifyTransactionType(t, categoryIndex);

                return {
                    id: t.id,
                    transactionId: displayReference(t),
                    type,
                    typeLabel,
                    amount: t.amount || t.netAmount || 0,
                    currency: t.currency || 'CDF',
                    status: t.status === 'completed' || t.status === 'COMPLETED' ? 'completed' : 
                            t.status === 'pending' || t.status === 'PENDING' ? 'pending' : 'failed',
                    method: resolveOperator({ name: t.provider_name || t.pos?.provider_name, providerId: t.provider_id, phone: t.pos?.payer_phone }) || '—',
                    provider: resolveOperator({ name: t.provider_name || t.pos?.provider_name, providerId: t.provider_id, phone: t.pos?.payer_phone }) || '—',
                    account: t.pos?.payer_phone || '—',
                    date: dateObj.toLocaleDateString('fr-FR'),
                    time: dateObj.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
                    fees: t.fees || 0,
                    netAmount: t.netAmount || t.amount || 0,
                    description: t.label || t.description || 'Transaction',
                    customer: displayCustomer(t),
                    reference: displayReference(t),
                    receiptId: t.receiptId || null,
                    raw: t,
                    accountableId,
                    cashierName,
                    caisseName,
                };
            };

            const transformedTransactions = transactions.map(transformTransaction);

            // Organiser par catégories (les filtres sont déjà appliqués côté API via slug, sts, srv, act, ref, opr)
            const allTransactions = transformedTransactions;
            setTransactionsData({
                all: allTransactions,
                paymentRequests: transformedTransactions.filter(t => t.type === 'paymentRequest' || t.type === 'payment'),
                invoices: transformedTransactions.filter(t => t.type === 'invoice'),
                paymentLinks: transformedTransactions.filter(t => t.type === 'paymentLink'),
                withdrawals: transformedTransactions.filter(t => t.type === 'withdrawal'),
                deposits: transformedTransactions.filter(t => t.type === 'deposit')
            });
        } catch (error) {
            console.error('Error loading transactions:', error);
            setTransactionsData({
                all: [],
                paymentRequests: [],
                invoices: [],
                paymentLinks: [],
                withdrawals: [],
                deposits: []
            });
        } finally {
            setLoading(false);
            setCurrencyLoading(false);
            hasLoadedOnceRef.current = true;
        }
    }, [selectedOrganization, timeRange, currency, filters, currentPage, user?.id, cashierNameById, caisseById, caisseNamesByCashier, categoryIndex]);

    loadTransactionsRef.current = loadTransactions;

    useEffect(() => {
        const refresh = () => {
            if (selectedOrganization?.id && loadTransactionsRef.current) {
                loadTransactionsRef.current({ silent: true });
            }
        };
        window.addEventListener('transaction:created', refresh);
        window.addEventListener('paymentRequest:approved', refresh);
        return () => {
            window.removeEventListener('transaction:created', refresh);
            window.removeEventListener('paymentRequest:approved', refresh);
        };
    }, [selectedOrganization?.id]);

    useEffect(() => {
        if (!selectedOrganization) {
            setLoading(false);
            setCurrencyLoading(false);
            setTransactionsData({ all: [], paymentRequests: [], invoices: [], paymentLinks: [], withdrawals: [], deposits: [] });
            return;
        }
        loadTransactions({ silent: hasLoadedOnceRef.current });
    }, [loadTransactions, selectedOrganization]);

    return (
        <div className="min-h-screen bg-white flex">
            <AdminSidebar
                isOpen={sidebarOpen}
                onToggle={() => setSidebarOpen(!sidebarOpen)}
                activeSection="transactions"
            />

            <div className="flex-1 min-h-screen transition-all duration-300 overflow-hidden">
                <div className="bg-gradient-to-br from-gray-50 via-white to-amber-50 min-h-screen p-4 lg:p-6">
                    <div className="relative z-10 space-y-6">
                        <DashboardHeader
                            timeRange={timeRange}
                            onTimeRangeChange={(v) => { setTimeRange(v); setCurrentPage(1); }}
                            currency={currency}
                            onCurrencyChange={(v) => { setCurrency(v); setCurrentPage(1); }}
                            currencyLoading={currencyLoading}
                            onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
                        />

                        {/* En-tête de la page */}
                        <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-6 text-amber-900 shadow-sm">
                            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                                <div className="flex-1">
                                    <div className="flex items-center space-x-4 mb-4">
                                        <div className="p-2 bg-amber-500/10 rounded-xl">
                                            <SwapHoriz className="text-xl text-amber-600" />
                                        </div>
                                        <div>
                                            <h1 className="text-2xl font-bold text-amber-900">
                                                Centre de Transactions
                                            </h1>
                                            <p className="text-amber-700">
                                                Gérez toutes vos activités financières en un seul endroit
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Statistiques */}
                        <TransactionStats
                            currency={currency}
                            timeRange={timeRange}
                            transactions={transactionsData.all || []}
                            loading={loading}
                        />

                        {/* Navigation par onglets */}
                        <TransactionTabs
                            activeTab={activeTab}
                            onTabChange={(tab) => {
                                setActiveTab(tab);
                                setCurrentPage(1);
                            }}
                            counts={{
                                all: transactionsData.all?.length || 0,
                                paymentRequests: transactionsData.paymentRequests?.length || 0,
                                invoices: transactionsData.invoices?.length || 0,
                                paymentLinks: transactionsData.paymentLinks?.length || 0
                            }}
                        />

                        {/* Filtres — même style que Liens de paiement */}
                        <TransactionFilters
                            activeTab={activeTab}
                            filters={filters}
                            onFiltersChange={(newFilters) => {
                                setFilters(newFilters);
                                setCurrentPage(1);
                            }}
                            caisses={caisses}
                            showCaisseFilter={isAdmin}
                            counts={(() => {
                                const list = transactionsData[activeTab] || [];
                                return {
                                    all: list.length,
                                    completed: list.filter(t => (t.status || '').toLowerCase() === 'completed').length,
                                    pending: list.filter(t => (t.status || '').toLowerCase() === 'pending').length,
                                    failed: list.filter(t => (t.status || '').toLowerCase() === 'failed').length,
                                    expired: list.filter(t => (t.status || '').toLowerCase() === 'expired').length
                                };
                            })()}
                        />

                        {/* Tableau des transactions */}
                        {!selectedOrganization ? (
                            <div className="bg-white rounded-2xl p-12 border border-gray-200 shadow-sm">
                                <div className="text-center text-gray-500">
                                    <SwapHoriz className="mx-auto mb-4 text-4xl text-amber-200" />
                                    <p className="font-medium">Sélectionnez une organisation pour afficher les transactions</p>
                                    <p className="text-sm mt-1">Les transactions sont chargées dynamiquement selon la période, la devise et les filtres.</p>
                                </div>
                            </div>
                        ) : loading && (transactionsData[activeTab] || []).length === 0 ? (
                            <div className="bg-white rounded-2xl p-12 border border-gray-200 shadow-sm">
                                <div className="text-center">
                                    <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                                    <p className="text-gray-600">Chargement des transactions...</p>
                                </div>
                            </div>
                        ) : (
                        <TransactionTable
                            transactions={transactionsData[activeTab] || []}
                            onTransactionSelect={setSelectedTransaction}
                            type={activeTab}
                            currentPage={currentPage}
                            nextPage={nextPage}
                            prevPage={prevPage}
                            onPageChange={setCurrentPage}
                            showCaisse={isAdmin}
                        />
                        )}

                        {/* Modal de détail */}
                        {selectedTransaction && (
                            <TransactionModal
                                transaction={selectedTransaction}
                                onClose={() => setSelectedTransaction(null)}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TransactionsPage;
